import * as THREE from "three";
import { DEG, G, V3 } from "./core";
import { PRIMITIVES } from "./primitives";
import { TRANSFORMER } from "./transformer";

/** @formigen/geometry — Design Model → THREE.Group.
 *  Pure: no DOM, no fetch, no engineering constants. */

function array(p, mat, M) {
  const g = G();
  const gen = GENERATORS[p.child?.gen];
  if (!gen) return g;
  const count = Math.max(1, Math.round(p.count || 1));
  const step = p.step || [0, 0, 0];
  const childMat = (p.child.mat && M[p.child.mat]) || mat;
  const proto = gen(p.child.params || {}, childMat, M);
  const meshes = [];
  proto.traverse((o) => { if (o.isMesh) meshes.push(o); });
  if (count >= 20 && meshes.length === 1) {
    const inst = new THREE.InstancedMesh(meshes[0].geometry, meshes[0].material, count);
    const m4 = new THREE.Matrix4();
    for (let i = 0; i < count; i++) {
      m4.makeTranslation(step[0] * i, step[1] * i, step[2] * i);
      m4.multiply(new THREE.Matrix4().compose(meshes[0].position, meshes[0].quaternion, meshes[0].scale));
      inst.setMatrixAt(i, m4);
    }
    inst.instanceMatrix.needsUpdate = true;
    g.add(inst);
    return g;
  }
  for (let i = 0; i < count; i++) {
    const c = i === 0 ? proto : gen(p.child.params || {}, childMat, M);
    c.position.set(step[0] * i, step[1] * i, step[2] * i);
    g.add(c);
  }
  return g;
}

export const GENERATORS = { ...PRIMITIVES, ...TRANSFORMER, array };

export function buildMaterials(spec = {}) {
  const M = {};
  for (const [k, m] of Object.entries(spec)) {
    M[k] = new THREE.MeshStandardMaterial({
      color: new THREE.Color(m.color),
      roughness: m.roughness ?? 0.62,
      metalness: m.metalness ?? 0.12,
      transparent: !!m.transparent,
      opacity: m.opacity ?? 1,
      side: m.double_sided ? THREE.DoubleSide : THREE.FrontSide,
    });
  }
  return M;
}

/** Explicit cartesian repeat: an axis array replaces that component of `at`;
 *  `mirror` duplicates the set with the sign flipped on that axis. */
function instancesOf(node) {
  const at = node.at || [0, 0, 0];
  const rot = node.rot || [0, 0, 0];
  const rep = node.repeat;
  if (!rep) return [{ at, rot }];
  const xs = rep.x || [at[0]], ys = rep.y || [at[1]], zs = rep.z || [at[2]];
  let out = [];
  for (const x of xs) for (const y of ys) for (const z of zs) out.push({ at: [x, y, z], rot });
  for (const ax of rep.mirror || []) {
    const i = ax === "x" ? 0 : ax === "y" ? 1 : 2;
    out = out.concat(out.map((o) => {
      const a = [...o.at]; a[i] = -a[i];
      const r = [...o.rot];
      if (rep.mirror_rotate) r[1] = (r[1] || 0) + 180;
      return { at: a, rot: r };
    }));
  }
  return out;
}

/** Build one part: a THREE.Group holding every node instance, its own cloned
 *  materials (so hover/selection/x-ray are per part) and its anchor table. */
export function buildPart(part, M) {
  const g = G();
  const anchorTable = {};
  for (const node of part.nodes || []) {
    const gen = GENERATORS[node.gen];
    if (!gen) { console.warn(`[formigen/geometry] unknown generator "${node.gen}"`); continue; }
    for (const inst of instancesOf(node)) {
      const child = gen(node.params || {}, M[node.mat] || M.steel, M);
      child.position.set(inst.at[0], inst.at[1], inst.at[2]);
      child.rotation.set((inst.rot[0] || 0) * DEG, (inst.rot[1] || 0) * DEG, (inst.rot[2] || 0) * DEG);
      child.updateMatrix();
      for (const [name, pos] of Object.entries(child.userData.anchors || {})) {
        const v = V3(pos[0], pos[1], pos[2]).applyMatrix4(child.matrix);
        (anchorTable[name] = anchorTable[name] || []).push(v);
      }
      if (node.shadow === false) child.userData.noShadow = true;
      g.add(child);
    }
  }
  // per-part materials: clone each source material once so emissive / opacity are isolated
  const seen = new Map();
  g.traverse((o) => {
    if (!o.isMesh) return;
    const src = o.material;
    if (!seen.has(src)) seen.set(src, src.clone());
    o.material = seen.get(src);
    o.userData.part = part.id;
  });
  const mats = [...seen.values()];
  mats.forEach((m) => { m.userData.baseOpacity = m.opacity; m.userData.baseTransparent = m.transparent; });
  g.visible = part.visible !== false;
  return { group: g, mats, anchors: anchorTable };
}

/** Build the whole Design Model. Returns the scene content plus a draw-call /
 *  triangle budget report (§7 budget: ≤1,500 draw calls, ≤1.5 M triangles). */
export function buildModel(model) {
  const M = buildMaterials(model.materials);
  const root = G();
  const parts = {};
  const meshes = [];
  for (const part of model.parts || []) {
    const built = buildPart(part, M);
    parts[part.id] = built;
    root.add(built.group);
    built.group.traverse((o) => {
      if (!o.isMesh) return;
      o.castShadow = !o.parent?.userData?.noShadow;
      o.receiveShadow = true;
      meshes.push(o);
    });
  }
  let tris = 0;
  for (const m of meshes) {
    const g = m.geometry;
    const c = g.index ? g.index.count / 3 : (g.attributes.position?.count || 0) / 3;
    tris += c * (m.isInstancedMesh ? m.count : 1);
  }
  return { root, parts, materials: M, meshes, stats: { drawCalls: meshes.length, triangles: Math.round(tris) } };
}
